import { Fragment, type JSX, useEffect, useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import {
  Button,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@caratuva/ui';
import type { ProductionAccessRequestDTO } from '@caratuva/shared-types';
import { getRole } from '../lib/auth';
import {
  getOrgFeeSchedule,
  listProductionAccessRequests,
  setOrgFeeSchedule,
  setProductionAccess,
} from '../lib/api';

export const Route = createFileRoute('/_app/admin/production-access')({
  // Client-side UX gate only — the real authorization is the server-side
  // @OpsOnly() guard on the ops endpoints, which 403s non-ops principals.
  beforeLoad: () => {
    if (getRole() !== 'ops') {
      throw redirect({ to: '/invoices' });
    }
  },
  component: AdminProductionAccessPage,
});

// Inline per-org volume-tier fee editor (ADR-0013). Pre-fills from the org's
// active schedule (or the defaults), lets ops negotiate the rate against the
// expected monthly volume shown on the request, and saves via the ops endpoint.
function FeeScheduleEditor({
  orgId,
  expectedMonthlyVolume,
}: {
  orgId: string;
  expectedMonthlyVolume: string;
}): JSX.Element {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['org-fee-schedule', orgId],
    queryFn: ({ signal }) => getOrgFeeSchedule(orgId, signal),
  });

  const [monthlyMin, setMonthlyMin] = useState('');
  const [tiers, setTiers] = useState<Array<{ minVolumeUsd: string; feeBps: string }>>([]);

  useEffect(() => {
    if (!query.data) return;
    setMonthlyMin(query.data.monthlyMinimumUsd ?? '');
    setTiers(
      query.data.tiers.map((t) => ({ minVolumeUsd: t.minVolumeUsd, feeBps: String(t.feeBps) })),
    );
  }, [query.data]);

  const save = useMutation({
    mutationFn: () =>
      setOrgFeeSchedule(orgId, {
        tiers: tiers.map((t) => ({
          minVolumeUsd: t.minVolumeUsd.trim(),
          feeBps: Number(t.feeBps),
        })),
        monthlyMinimumUsd: monthlyMin.trim() === '' ? null : monthlyMin.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['org-fee-schedule', orgId] });
      toast.success(
        intl.formatMessage({ id: 'admin.fees.saved', defaultMessage: 'Fee schedule saved.' }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function updateTier(i: number, patch: Partial<{ minVolumeUsd: string; feeBps: string }>): void {
    setTiers((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  }

  if (query.isLoading) {
    return (
      <p className="text-sm text-navy-500">
        {intl.formatMessage({ id: 'common.loading', defaultMessage: 'Loading…' })}
      </p>
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-navy-100 bg-navy-50/40 p-4">
      <p className="text-sm text-navy-600">
        {intl.formatMessage(
          {
            id: 'admin.fees.hint',
            defaultMessage:
              'Negotiate the volume-tier rate for this org. Expected volume/mo from the request: {vol} USD.',
          },
          { vol: expectedMonthlyVolume },
        )}
      </p>

      <div className="w-48">
        <Label htmlFor={`min-${orgId}`}>
          {intl.formatMessage({
            id: 'admin.fees.monthlyMin',
            defaultMessage: 'Monthly minimum (USD, optional)',
          })}
        </Label>
        <Input
          id={`min-${orgId}`}
          type="number"
          min="0"
          step="0.01"
          value={monthlyMin}
          onChange={(e) => setMonthlyMin(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        {tiers.map((t, i) => (
          <div key={i} className="flex items-end gap-2">
            <div className="flex-1">
              {i === 0 ? (
                <Label>
                  {intl.formatMessage({
                    id: 'admin.fees.tier.volume',
                    defaultMessage: 'Volume from (USD)',
                  })}
                </Label>
              ) : null}
              <Input
                type="number"
                min="0"
                step="0.01"
                value={t.minVolumeUsd}
                onChange={(e) => updateTier(i, { minVolumeUsd: e.target.value })}
              />
            </div>
            <div className="w-32">
              {i === 0 ? (
                <Label>
                  {intl.formatMessage({ id: 'admin.fees.tier.bps', defaultMessage: 'Fee (bps)' })}
                </Label>
              ) : null}
              <Input
                type="number"
                min="0"
                max="1000"
                value={t.feeBps}
                onChange={(e) => updateTier(i, { feeBps: e.target.value })}
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={tiers.length <= 1}
              onClick={() => setTiers((prev) => prev.filter((_, idx) => idx !== i))}
            >
              {intl.formatMessage({ id: 'admin.fees.tier.remove', defaultMessage: 'Remove' })}
            </Button>
          </div>
        ))}
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setTiers((prev) => [...prev, { minVolumeUsd: '', feeBps: '' }])}
        >
          {intl.formatMessage({ id: 'admin.fees.tier.add', defaultMessage: 'Add tier' })}
        </Button>
      </div>

      <Button type="button" size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
        {intl.formatMessage({ id: 'admin.fees.save', defaultMessage: 'Save fee schedule' })}
      </Button>
    </div>
  );
}

function AdminProductionAccessPage(): JSX.Element {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [editingOrgId, setEditingOrgId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin-production-access'],
    queryFn: ({ signal }) => listProductionAccessRequests(signal),
  });

  const decide = useMutation({
    mutationFn: ({ orgId, enabled }: { orgId: string; enabled: boolean }) =>
      setProductionAccess(orgId, enabled),
    onSuccess: (_res, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-production-access'] });
      toast.success(
        vars.enabled
          ? intl.formatMessage({
              id: 'admin.prodAccess.approved',
              defaultMessage: 'Production access approved.',
            })
          : intl.formatMessage({
              id: 'admin.prodAccess.rejected',
              defaultMessage: 'Request rejected.',
            }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const rows: ProductionAccessRequestDTO[] = query.data ?? [];

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-navy-900">
        {intl.formatMessage({
          id: 'admin.prodAccess.title',
          defaultMessage: 'Production access requests',
        })}
      </h1>
      <p className="text-sm text-navy-600">
        {intl.formatMessage({
          id: 'admin.prodAccess.description',
          defaultMessage:
            'Pending requests to enable live mode. Approve once the sales conversation and fees are settled — approval unlocks live KYB and real money movement.',
        })}
      </p>

      {query.isLoading ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({ id: 'common.loading', defaultMessage: 'Loading…' })}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-navy-500">
          {intl.formatMessage({
            id: 'admin.prodAccess.empty',
            defaultMessage: 'No pending requests.',
          })}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                {intl.formatMessage({
                  id: 'admin.prodAccess.col.company',
                  defaultMessage: 'Company',
                })}
              </TableHead>
              <TableHead>
                {intl.formatMessage({ id: 'admin.prodAccess.col.cnpj', defaultMessage: 'CNPJ' })}
              </TableHead>
              <TableHead>
                {intl.formatMessage({
                  id: 'admin.prodAccess.col.contact',
                  defaultMessage: 'Contact',
                })}
              </TableHead>
              <TableHead>
                {intl.formatMessage({
                  id: 'admin.prodAccess.col.volume',
                  defaultMessage: 'Volume/mo (USD)',
                })}
              </TableHead>
              <TableHead>
                {intl.formatMessage({
                  id: 'admin.prodAccess.col.corridors',
                  defaultMessage: 'Corridors',
                })}
              </TableHead>
              <TableHead>
                {intl.formatMessage({
                  id: 'admin.prodAccess.col.actions',
                  defaultMessage: 'Actions',
                })}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <Fragment key={r.id}>
                <TableRow>
                  <TableCell>
                    <div className="font-medium text-navy-900">{r.legalName}</div>
                    <div className="text-xs text-navy-500">{r.orgId}</div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.cnpj}</TableCell>
                  <TableCell className="text-sm">{r.businessContactEmail}</TableCell>
                  <TableCell className="text-sm">{r.expectedMonthlyVolume}</TableCell>
                  <TableCell className="text-sm">{r.corridors.join(', ')}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ orgId: r.orgId, enabled: true })}
                      >
                        {intl.formatMessage({
                          id: 'admin.prodAccess.approve',
                          defaultMessage: 'Approve',
                        })}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ orgId: r.orgId, enabled: false })}
                      >
                        {intl.formatMessage({
                          id: 'admin.prodAccess.reject',
                          defaultMessage: 'Reject',
                        })}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingOrgId((cur) => (cur === r.orgId ? null : r.orgId))}
                      >
                        {intl.formatMessage({
                          id: 'admin.prodAccess.fees',
                          defaultMessage: 'Fees',
                        })}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {editingOrgId === r.orgId ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <FeeScheduleEditor
                        orgId={r.orgId}
                        expectedMonthlyVolume={r.expectedMonthlyVolume}
                      />
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
